import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LanguageSwitch } from '@/components/ui/LanguageSwitch';
import { Menu } from '@/components/ui/Menu';
import { LOCALE_NAMES, useLocalePreference } from './useLocalePreference';
import { NotificationBell } from './NotificationBell';
import type { Wedding } from '@guabi/shared';
import { Logo, Wordmark } from '@/components/ui/Logo';
import { Button } from '@/components/ui/Button';
import { longDate } from '@/lib/format';
import { useAuth } from '@/features/auth/AuthProvider';
import { EditWeddingDrawer } from './EditWeddingDrawer';
import { MembersDrawer } from '@/features/members/MembersDrawer';

export function TopBar({
  wedding,
  weddings,
  onSwitch,
}: {
  wedding?: Wedding;
  weddings: Wedding[];
  onSwitch: (wedId: string) => void;
}) {
  const { t } = useTranslation();
  const auth = useAuth();
  const [editing, setEditing] = useState(false);
  const [sharing, setSharing] = useState(false);
  // One tap flips between the two languages rather than opening a select inside
  // a menu, which on a phone is a popover inside a popover.
  const locale = useLocalePreference();

  return (
    /*
     * The top inset matters only when installed. iOS is told
     * `apple-mobile-web-app-status-bar-style: black-translucent`, so a standalone
     * Guabi draws under the clock and the notch — without this padding the
     * wedding's name sits behind them. It is 0 in a browser and on a phone with
     * no notch, so it costs nothing anywhere else.
     */
    <header className="border-b border-line bg-paper pt-[env(safe-area-inset-top)]">
      <div className="gutter mx-auto flex w-full max-w-[1400px] flex-wrap items-center gap-3 py-3">
        <div className="flex items-center gap-2">
          <Logo size={32} tone="leaf" />
          <Wordmark className="text-leaf" />
        </div>

        <div className="h-6 w-px bg-line" aria-hidden="true" />

        {weddings.length > 1 ? (
          <label className="flex items-center gap-2 text-caption text-leaf-soft">
            <span className="sr-only">Wedding</span>
            <select
              value={wedding?.wedId ?? ''}
              onChange={(e) => onSwitch(e.target.value)}
              className="min-h-tap rounded border border-line bg-paper px-2 font-display text-[18px]"
            >
              {weddings.map((w) => (
                <option key={w.wedId} value={w.wedId}>
                  {w.title}
                </option>
              ))}
            </select>
          </label>
        ) : (
          wedding && (
            <div className="flex flex-col leading-tight">
              <span className="font-display text-[20px]">{wedding.title}</span>
              <span className="text-caption text-leaf-soft">
                {wedding.date ? longDate(wedding.date) : t('home.noDate')}
              </span>
            </div>
          )
        )}

        {/* Six controls in one row is a desktop header. On a phone they wrapped
            onto three lines of chrome before any content, so everything but the
            bell goes behind one button. */}
        <div className="ml-auto flex items-center gap-3">
          <span className="hidden text-caption text-leaf-soft lg:inline">{auth.email}</span>
          {wedding && <NotificationBell wedId={wedding.wedId} />}

          <div className="hidden items-center gap-3 sm:flex">
            {wedding && (
              <Button variant="secondary" onClick={() => setSharing(true)}>
                {t('members.share')}
              </Button>
            )}
            {wedding && (
              <Button variant="secondary" onClick={() => setEditing(true)}>
                {t('wedding.edit')}
              </Button>
            )}
            <LanguageSwitch />
            <Button variant="secondary" onClick={() => void auth.signOut()}>
              {t('auth.signOut')}
            </Button>
          </div>

          <div className="sm:hidden">
            <Menu
              variant="secondary"
              trigger={
                <>
                  <span className="sr-only">{t('nav.more')}</span>
                  <svg aria-hidden="true" viewBox="0 0 20 20" className="size-5">
                    <circle cx="4" cy="10" r="1.5" fill="currentColor" />
                    <circle cx="10" cy="10" r="1.5" fill="currentColor" />
                    <circle cx="16" cy="10" r="1.5" fill="currentColor" />
                  </svg>
                </>
              }
              className="px-3"
              sections={[
                {
                  items: [
                    ...(wedding
                      ? [
                          { label: t('members.share'), onSelect: () => setSharing(true) },
                          { label: t('wedding.edit'), onSelect: () => setEditing(true) },
                        ]
                      : []),
                    {
                      label: LOCALE_NAMES[locale.other],
                      onSelect: () => locale.change(locale.other),
                    },
                    { label: t('auth.signOut'), onSelect: () => void auth.signOut() },
                  ],
                },
              ]}
            />
          </div>
        </div>
      </div>

      {wedding && (
        <EditWeddingDrawer
          key={wedding.updatedAt}
          wedding={wedding}
          open={editing}
          onClose={() => setEditing(false)}
        />
      )}

      {wedding && (
        <MembersDrawer
          wsId={wedding.wsId}
          open={sharing}
          onClose={() => setSharing(false)}
          myUserId={auth.userId}
        />
      )}
    </header>
  );
}
